import { useAuth } from "@dl/web-core-js/auth";
import { PageHeader } from "@dl/web-core-js/shell";
import { columns as defineColumns, DataTable } from "@dl/web-core-js/table";
import { Badge, Button, Dialog, Field, saveBlob, Select, useConfirm } from "@dl/web-core-js/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Download, Eye, UploadCloud, Wrench } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  InventoryUpdateClient,
  SalesChannelClient,
  type InventoryPreviewDTO,
  type InventoryPreviewLineDTO,
  type InventoryReason,
  type InventoryUpdateDTO,
  type InventoryUpdateLineDTO,
  type SalesChannelDTO,
} from "../api/inventory";
import { NexpimView } from "../views";
import { BundleItemNote, ReasonBadge } from "./inventory/reasons";
import { InventoryRuleJobDialog } from "./InventoryRuleJobDialog";

/**
 * The feed-run log: every push of stock levels to a sales channel, what each
 * one published per SKU and why, and the controls to run or preview one now.
 *
 * A preview is a dry run — the server resolves every line and builds the
 * request or file exactly as a run would, then delivers nothing and records
 * nothing. That is the way to check a channel before switching it on, and the
 * way to see what a run WOULD say without mailing a customer.
 *
 * The run itself is a synchronous round-trip to the platform; the button
 * shows its own progress and the rest of the page stays usable.
 */
export function ApiInventoryUpdatesPage() {
  const { t } = useTranslation();
  const { http, hasView } = useAuth();
  const confirm = useConfirm();
  const queryClient = useQueryClient();

  const client = useMemo(() => new InventoryUpdateClient(http), [http]);
  const channelClient = useMemo(() => new SalesChannelClient(http), [http]);
  const allowed = hasView(NexpimView.API_INVENTORY_UPDATES);

  const [pushOpen, setPushOpen] = useState(false);
  const [code, setCode] = useState<string>("");
  const [preview, setPreview] = useState<InventoryPreviewDTO | null>(null);
  const [run, setRun] = useState<InventoryUpdateDTO | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["inventory-update", "all"],
    queryFn: () => client.findAll(),
    enabled: allowed,
  });
  const { data: channels } = useQuery({
    queryKey: ["sales-channel", "all"],
    queryFn: () => channelClient.findAll(),
    enabled: allowed,
    staleTime: 30 * 60_000,
  });

  const selectedCode = code || channels?.find((channel) => channel.active)?.code || channels?.[0]?.code || "";
  const selected = channels?.find((channel) => channel.code === selectedCode);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["inventory-update", "all"] });

  const push = useMutation({
    mutationFn: (target: string) => client.updateChannel(target),
    onSuccess: async (result) => {
      setPushOpen(false);
      await refresh();
      const name = result.sales_channel_name ?? result.sales_channel_code ?? "";
      if (result.api_response_success) {
        toast.success(t("inv-update.done", "{{platform}} updated ({{count}} lines)", { platform: name, count: result.line_count ?? 0 }));
      } else {
        toast.error(
          t("inv-update.failed", "{{platform}} update failed (HTTP {{status}})", {
            platform: name,
            status: result.api_response_http_status ?? "",
          }),
        );
      }
      setRun(result);
    },
    onError: (failure) => toast.error(failure instanceof Error ? failure.message : t("error.generic", "Failed")),
  });

  const previewRun = useMutation({
    mutationFn: (target: string) => client.preview(target),
    onSuccess: (result) => setPreview(result),
    onError: (failure) => toast.error(failure instanceof Error ? failure.message : t("error.generic", "Failed")),
  });

  const [rulesOpen, setRulesOpen] = useState(false);

  const cols = useMemo(() => {
    const c = defineColumns<InventoryUpdateDTO>();
    return [
      c.text("channel", (row) => row.sales_channel_name ?? row.sales_channel_code ?? "", {
        headerKey: "inv-update.platform",
        filter: "select",
        priority: 1,
      }),
      c.custom(
        "result",
        (row) => (row.api_response_success ? "OK" : "FAILED"),
        (row) => (
          <Badge variant={row.api_response_success ? "success" : "error"} size="sm">
            {row.api_response_success
              ? t("inv-update.ok", "OK")
              : `${t("inv-update.error", "FAILED")} ${row.api_response_http_status ?? ""}`}
          </Badge>
        ),
        { headerKey: "inv-update.result", filter: "select", priority: 2 },
      ),
      c.number("lines", (row) => row.line_count ?? 0, {
        headerKey: "inv-update.lines",
        filter: "range",
        priority: 2,
      }),
      c.custom("created_at", (row) => isoDay(row.created_at), (row) => stamp(row.created_at), {
        headerKey: "inv-update.started",
        filter: "daterange",
        priority: 3,
      }),
      c.custom("completed_at", (row) => isoDay(row.completed_at), (row) => stamp(row.completed_at), {
        headerKey: "inv-update.completed",
        filter: "daterange",
      }),
      // How long the channel took to answer — the number that tells you a
      // platform is degrading before it starts failing outright.
      c.number("elapsed", (row) => elapsedSeconds(row.created_at, row.completed_at), {
        headerKey: "column.elapsed-time",
        filter: "range",
        priority: 4,
      }),
      c.number("http_status", (row) => row.api_response_http_status ?? null, {
        headerKey: "column.api-response-http-status",
        defaultVisible: false,
      }),
    ];
  }, [t]);

  if (!allowed) {
    return (
      <>
        <PageHeader title={t("navigation.item.api-inventory-updates", "Inventory updates")} />
        <div className="p-4 lg:p-6">
          <p className="text-[13px] text-ink-muted">
            {t("error.access-denied.detail", "Your account does not have permission to view this page.")}
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t("navigation.item.api-inventory-updates", "Inventory updates")}
        subtitle={t("inv-update.subtitle", "Stock feeds to the sales channels: every run, every SKU, and why.")}
      />

      <DataTable
        data={data ?? []}
        columns={cols}
        getRowId={(row) => String(row.id)}
        loading={isLoading}
        selectable
        onRowClick={(row) => setRun(row)}
        actions={[
          {
            label: t("inv-update.push", "Run channel"),
            icon: <UploadCloud className="size-4" />,
            variant: "primary",
            write: true,
            disabled: push.isPending,
            onClick: () => setPushOpen(true),
          },
          {
            label: t("inv-rules.title", "Apply inventory rules"),
            icon: <Wrench className="size-4" />,
            variant: "secondary",
            write: true,
            onClick: () => setRulesOpen(true),
          },
        ]}
        onDeleteSelected={async (rows) => {
          await client.removeBatch(rows.map((row) => row.id));
          await refresh();
          toast.success(t("notification.deleted", "Deleted"));
        }}
        emptyMessage={t("table.empty", "Nothing to show")}
      />

      <InventoryRuleJobDialog open={rulesOpen} onClose={() => setRulesOpen(false)} />

      <Dialog
        open={pushOpen}
        onClose={() => setPushOpen(false)}
        title={t("inv-update.push", "Run channel")}
        description={t(
          "inv-update.push.description",
          "Preview resolves every SKU and builds the feed without sending anything. Run delivers it (API call or mail) and records the result.",
        )}
        size="sm"
        actions={
          <>
            <Button variant="secondary" onClick={() => setPushOpen(false)} disabled={push.isPending}>
              {t("button.cancel", "Cancel")}
            </Button>
            <Button
              variant="secondary"
              loading={previewRun.isPending}
              disabled={!selectedCode || push.isPending}
              onClick={() => previewRun.mutate(selectedCode)}
            >
              <Eye className="size-4" />
              {t("inv-update.preview", "Preview")}
            </Button>
            <Button
              variant="primary"
              loading={push.isPending}
              disabled={!selectedCode || !selected?.active || previewRun.isPending}
              onClick={async () => {
                const confirmed = await confirm({
                  title: t("inv-update.confirm", "Push inventory to {{platform}}?", { platform: selected?.name ?? selectedCode }),
                  message:
                    selected?.transport === "MAIL"
                      ? t("inv-update.confirm.mail", "This mails the file to {{to}}.", { to: selected.mail_to.join(", ") })
                      : t("inv-update.confirm.api", "This calls the platform's API."),
                });
                if (confirmed) push.mutate(selectedCode);
              }}
            >
              {t("inv-update.run", "Run")}
            </Button>
          </>
        }
      >
        <Field
          label={t("inv-update.platform", "Channel")}
          htmlFor="push-channel"
          hint={selected ? channelHint(t, selected) : undefined}
        >
          <Select
            id="push-channel"
            value={selectedCode}
            options={(channels ?? []).map((channel) => ({
              value: channel.code,
              label: channel.active ? channel.name : `${channel.name} (${t("inv-update.channel.inactive", "inactive")})`,
            }))}
            onChange={(event) => setCode(event.target.value)}
          />
        </Field>
      </Dialog>

      {preview && (
        <PreviewDialog
          preview={preview}
          onClose={() => setPreview(null)}
          onDownload={async () => {
            const { blob, filename } = await client.previewAttachment(preview.sales_channel.code);
            saveBlob(blob, filename);
          }}
        />
      )}

      {run && <RunDialog run={run} client={client} onClose={() => setRun(null)} />}
    </>
  );
}

function channelHint(t: (key: string, fallback: string, options?: Record<string, unknown>) => string, channel: SalesChannelDTO): string {
  const parts = [
    channel.market
      ? t("inv-update.channel.market", "Market {{market}}", { market: channel.market })
      : t("inv-update.channel.no-market", "No market: client not found"),
    channel.transport === "MAIL"
      ? t("inv-update.channel.mail", "mailed to {{to}}", { to: channel.mail_to.join(", ") })
      : channel.transport,
    channel.client?.cli_name1 ?? "",
    channel.delist_after_sends
      ? t("inv-update.channel.delist", "discontinued SKUs sent {{count}}× then delisted", { count: channel.delist_after_sends })
      : t("inv-update.channel.never-delist", "discontinued SKUs always sent"),
  ];
  return parts.filter(Boolean).join(" · ");
}

/** The dry run: lines with reasons, the request an API channel would send, the file a MAIL channel would attach. */
function PreviewDialog({
  preview,
  onClose,
  onDownload,
}: {
  preview: InventoryPreviewDTO;
  onClose: () => void;
  onDownload: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const [downloading, setDownloading] = useState(false);
  const summary = useMemo(() => summarize(preview.lines), [preview.lines]);

  return (
    <Dialog
      open
      onClose={onClose}
      title={t("inv-update.preview.title", "Preview · {{platform}}", { platform: preview.sales_channel.name })}
      description={t("inv-update.preview.description", "Nothing was sent or recorded.")}
      size="xl"
      actions={
        <>
          {preview.attachment_name && (
            <Button
              variant="secondary"
              loading={downloading}
              onClick={async () => {
                setDownloading(true);
                try {
                  await onDownload();
                } finally {
                  setDownloading(false);
                }
              }}
            >
              <Download className="size-4" />
              {preview.attachment_name}
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            {t("button.close", "Close")}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <ReasonSummary summary={summary} />
        <LinesTable lines={preview.lines} withDispatchable />
        {preview.request && <PayloadBlock label={t("inv-update.request", "Request")} value={preview.request} />}
      </div>
    </Dialog>
  );
}

/** A recorded run: what went out, what came back, and every line it decided. */
function RunDialog({
  run,
  client,
  onClose,
}: {
  run: InventoryUpdateDTO;
  client: InventoryUpdateClient;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { data: lines, isLoading } = useQuery({
    queryKey: ["inventory-update-line", run.id],
    queryFn: () => client.lines(run.id),
  });
  const summary = useMemo(() => summarize(lines ?? []), [lines]);

  return (
    <Dialog
      open
      onClose={onClose}
      title={`${run.sales_channel_name ?? run.sales_channel_code ?? ""} · ${stamp(run.created_at)}`}
      size="xl"
      actions={
        <Button variant="secondary" onClick={onClose}>
          {t("button.close", "Close")}
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        <ReasonSummary summary={summary} />
        <LinesTable lines={lines ?? []} loading={isLoading} />
        <PayloadBlock label={t("inv-update.request", "Request")} value={run.api_request} />
        <PayloadBlock label={t("inv-update.response", "Response")} value={run.api_response} />
      </div>
    </Dialog>
  );
}

type AnyLine = InventoryUpdateLineDTO | InventoryPreviewLineDTO;

function LinesTable({ lines, loading, withDispatchable }: { lines: AnyLine[]; loading?: boolean; withDispatchable?: boolean }) {
  const { t } = useTranslation();
  const cols = useMemo(() => {
    const c = defineColumns<AnyLine>();
    const list = [
      c.text("sku", (row) => row.sku, { headerKey: "column.sku", priority: 1 }),
      c.number("quantity", (row) => row.quantity, { headerKey: "inv-update.line.quantity", filter: "range", priority: 1 }),
      c.custom(
        "reason",
        (row) => row.reason,
        (row) => <ReasonBadge reason={row.reason} />,
        { headerKey: "inv-update.line.reason", filter: "select", priority: 1 },
      ),
      // A bundle's line names the single item behind its figure: the one that
      // blocks it, or the scarcest one that sets its quantity.
      c.custom(
        "detail",
        (row) => row.detail ?? "",
        (row) => <BundleItemNote reason={row.reason} detail={row.detail} />,
        { headerKey: "inv-update.line.item", filter: "none", priority: 2 },
      ),
      c.custom(
        "discontinued",
        (row) => (row.discontinued ? "yes" : "no"),
        (row) => (row.discontinued ? <Badge variant="error" size="sm">{t("inv-update.line.discontinued", "discontinued")}</Badge> : null),
        { headerKey: "inv-update.line.discontinued", filter: "select", priority: 3 },
      ),
    ];
    if (withDispatchable) {
      list.splice(
        2,
        0,
        c.number("dispatchable", (row) => ("dispatchable" in row ? row.dispatchable : null), {
          headerKey: "inv-update.line.dispatchable",
          filter: "range",
          priority: 2,
        }),
      );
    }
    return list;
  }, [t, withDispatchable]);

  // A fixed-height flex column: the table scrolls its own body under a sticky
  // header. A max-height + overflow wrapper would scroll the toolbar and
  // header away with the rows.
  return (
    <div className="flex h-[50vh] flex-col overflow-hidden rounded-md border border-line">
      <DataTable
        data={lines}
        columns={cols}
        getRowId={(row) => row.sku}
        loading={loading}
        emptyMessage={t("inv-update.line.none", "No lines")}
      />
    </div>
  );
}

/** Counts per reason, so a run reads at a glance: "1,204 dispatchable, 38 out of stock, 2,229 discontinued". */
function summarize(lines: AnyLine[]): Array<{ reason: InventoryReason; count: number }> {
  const counts = new Map<InventoryReason, number>();
  for (const line of lines) counts.set(line.reason, (counts.get(line.reason) ?? 0) + 1);
  return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([reason, count]) => ({ reason, count }));
}

function ReasonSummary({ summary }: { summary: Array<{ reason: InventoryReason; count: number }> }) {
  if (summary.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {summary.map(({ reason, count }) => (
        <span key={reason} className="inline-flex items-center gap-1">
          <ReasonBadge reason={reason} />
          <span className="text-[12px] text-ink-muted">{count.toLocaleString()}</span>
        </span>
      ))}
    </div>
  );
}

function PayloadBlock({ label, value }: { label: string; value: string | null | undefined }) {
  const pretty = useMemo(() => {
    if (!value) return "";
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  }, [value]);

  return (
    <div>
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-ink-faint">{label}</p>
      <pre className="max-h-64 overflow-auto rounded-md border border-line bg-surface-sunk p-3 text-[12px] text-ink">
        {pretty}
      </pre>
    </div>
  );
}

/** Seconds between the push starting and the channel answering. */
function elapsedSeconds(start: string | null | undefined, end: string | null | undefined): number | null {
  if (!start || !end) return null;
  const from = new Date(start).getTime();
  const to = new Date(end).getTime();
  if (Number.isNaN(from) || Number.isNaN(to)) return null;
  return Math.round((to - from) / 1000);
}

function stamp(value: string | null | undefined): string {
  if (!value) return "";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "" : parsed.toLocaleString();
}

/**
 * `stamp`'s locale-formatted string isn't sortable or filterable as-is (it
 * reorders day/month by locale and isn't lexicographic), so the daterange
 * filter and the column's sort key use this ISO day instead, while the cell
 * keeps showing `stamp`'s friendlier local timestamp.
 */
function isoDay(value: string | null | undefined): string {
  if (!value) return "";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? "" : parsed.toISOString().slice(0, 10);
}
